import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import BackHomeButton from '../components/BackHomeButton';
import { useTranslation } from 'react-i18next';
import './Home.css';

function AllJobs() {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchLocation, setSearchLocation] = useState('');
  const [searchQualification, setSearchQualification] = useState('');
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [qualifications, setQualifications] = useState([]);

  useEffect(() => {
    let mounted = true;
    const fetchJobs = async () => {
      try {
        const res = await fetch('https://tstranscareers.in/api/api/jobs');
        const data = await res.json();
        if (!mounted) return;
        if (data.success) {
          setJobs(data.jobs || []);
        } else {
          setJobs([]);
        }
      } catch (err) {
        if (!mounted) return;
        setError('Failed to load jobs');
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    };

    fetchJobs();
    return () => { mounted = false; };
  }, []);

  // determine items per page based on viewport width
  const getItemsPerPage = () => {
    if (typeof window === 'undefined') return 10;
    const w = window.innerWidth;
    if (w <= 768) return 5; // mobile
    if (w <= 1024) return 7; // tablet
    return 10; // desktop
  };

  useEffect(() => {
    // set initial itemsPerPage and update on resize
    const onResize = () => {
      const ipp = getItemsPerPage();
      setItemsPerPage(ipp);
      setCurrentPage(1);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // adjust current page when jobs or itemsPerPage change
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil((jobs && jobs.length) / itemsPerPage));
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [jobs, itemsPerPage]);

  // derive filter options when jobs change
  useEffect(() => {
    const locSet = new Set();
    const qualSet = new Set();
    jobs.forEach((j) => {
      if (j.district) locSet.add(j.district);
      if (j.place) locSet.add(j.place);
      if (j.qualification) qualSet.add(j.qualification);
    });
    setLocations(Array.from(locSet).filter(Boolean).sort());
    setQualifications(Array.from(qualSet).filter(Boolean).sort());
  }, [jobs]);

  // apply filters
  useEffect(() => {
    const filtered = jobs.filter((j) => {
      let ok = true;
      if (searchLocation) {
        const loc = (j.district || j.place || '').toString().toLowerCase();
        ok = ok && loc.includes(searchLocation.toLowerCase());
      }
      if (searchQualification) {
        const q = (j.qualification || '').toString().toLowerCase();
        ok = ok && q.includes(searchQualification.toLowerCase());
      }
      return ok;
    });
    setFilteredJobs(filtered);
    setCurrentPage(1);
  }, [jobs, searchLocation, searchQualification]);

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}>{t('Loading...') || 'Loading...'}</div>;
  if (error) return <div style={{ padding: '2rem', color: 'red', textAlign: 'center' }}>{error}</div>;

  return (
    <div className="recent-jobs-section">
      <div className="container">
        <BackHomeButton style={{ margin: '1rem 0', padding: '0.5rem 1rem' }} />
        <h2 style={{ textAlign: 'center' }}>{t('All Jobs') || 'All Jobs'}</h2>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', margin: '1rem 0' }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>Location</label>
            <select value={searchLocation} onChange={(e) => setSearchLocation(e.target.value)} style={{ padding: '0.5rem 0.6rem' }}>
              <option value="">All locations</option>
              {locations.map((loc) => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>Qualification</label>
            <select value={searchQualification} onChange={(e) => setSearchQualification(e.target.value)} style={{ padding: '0.5rem 0.6rem' }}>
              <option value="">All qualifications</option>
              {qualifications.map((q) => (
                <option key={q} value={q}>{q}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button onClick={() => { setSearchLocation(''); setSearchQualification(''); }} style={{ padding: '0.5rem 0.75rem', background: '#f1f1f1', border: '1px solid #ddd', borderRadius: '4px' }}>Clear</button>
          </div>
        </div>
        {jobs.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
            <p>{t('No jobs available at the moment.') || 'No jobs available at the moment.'}</p>
          </div>
        ) : (
          <>
          <div className="jobs-grid">
            {/** slice jobs for current page */}
            {/** use filteredJobs when present */}
            {(filteredJobs && filteredJobs.length ? filteredJobs : jobs).slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((job) => (
              <div key={job.id} className="job-card">
                <div className="job-header">
                  <h3>{job.job_title || job.title}</h3>
                  <span className="company">{job.company_name || job.company}</span>
                </div>
                <div className="job-details">
                  <div className="detail-item">
                    <i className="fas fa-map-marker-alt"></i>
                    <span>{job.place}, {job.district}</span>
                  </div>
                  <div className="detail-item">
                    <i className="fas fa-graduation-cap"></i>
                    <span>{job.qualification}</span>
                  </div>
                  <div className="detail-item">
                    <i className="fas fa-money-bill-wave"></i>
                    <span>₹{job.annual_salary_from} - ₹{job.annual_salary_to}</span>
                  </div>
                  <div className="detail-item">
                    <i className="fas fa-clock"></i>
                    <span>{new Date(job.created_at || job.postedDate).toLocaleDateString()}</span>
                  </div>
                </div>
                <button className="view-job-btn" onClick={() => window.location.href = '/login'}>{t('Apply')}</button>
              </div>
            ))}
          </div>

          {/* pagination controls */}
          <div className="pagination" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{ padding: '0.4rem 0.75rem', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
            >
              Prev
            </button>
            {Array.from({ length: Math.max(1, Math.ceil((filteredJobs && filteredJobs.length ? filteredJobs.length : jobs.length) / itemsPerPage)) }, (_, i) => i + 1).map((pg) => (
              <button
                key={pg}
                onClick={() => setCurrentPage(pg)}
                aria-current={pg === currentPage ? 'page' : undefined}
                style={{
                  padding: '0.35rem 0.6rem',
                  background: pg === currentPage ? '#4169E1' : 'transparent',
                  color: pg === currentPage ? '#fff' : '#333',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {pg}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage((p) => p + 1)}
              disabled={currentPage >= Math.ceil(jobs.length / itemsPerPage)}
              style={{ padding: '0.4rem 0.75rem', cursor: currentPage >= Math.ceil(jobs.length / itemsPerPage) ? 'not-allowed' : 'pointer' }}
            >
              Next
            </button>
          </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AllJobs;
